import {
  Component,
  OnInit,
  ChangeDetectorRef,
  NgZone,
  ViewChild,
  TemplateRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';

import { UserService } from '../../../../services/user.service';
import { StudentService, Student } from '../../../../services/student.service';
import { ProfileService } from '../../../../services/profile.service';
import { InterestService } from '../../../../services/interests.service';
import { Profile } from '../../../../models/profile';
import { AvatarService, Avatar } from '../../../../services/avatar.service';

import { StudentNav } from '../../../common/student-nav/student-nav';
import { CounselorNav } from '../../counselor-view/counselor-nav/counselor-nav';
import { CAREERS } from '../../../common/profile-setup-dialog/profile-setup.constants';
import { AvatarBuilder, AvatarConfig } from '../../../common/avatar-builder/avatar-builder';
import { Interest } from '../../../../models/interest';
import { UserAvatar } from '../../../common/user-avatar/user-avatar';

@Component({
  selector: 'app-my-profile',
  imports: [
    CommonModule,
    FormsModule,
    StudentNav,
    CounselorNav,
    AvatarBuilder,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatProgressBarModule,
    MatDividerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    UserAvatar,
  ],
  templateUrl: './my-profile.html',
  styleUrl: './my-profile.css',
})
export class MyProfile implements OnInit {
  /** Template refs for each edit dialog, opened via `openModal()`. */
  @ViewChild('basicDialog') basicDialog!: TemplateRef<any>;
  @ViewChild('aboutDialog') aboutDialog!: TemplateRef<any>;
  @ViewChild('detailsDialog') detailsDialog!: TemplateRef<any>;
  @ViewChild('interestsDialog') interestsDialog!: TemplateRef<any>;
  @ViewChild('avatarDialog') avatarDialog!: TemplateRef<any>;

  /** Service for fetching all available interests and the student's selected ones. */
  interestService = inject(InterestService);

  /** The current student record, loaded on init. */
  student: Student | null = null;

  /** The current student's profile record, loaded on init. */
  profile: Profile | null = null;

  /** The current student's avatar record, loaded on init if one exists. */
  avatar: Avatar | null = null;

  /** Timestamp appended to the avatar image URL to bust the cache after a snapshot save. */
  avatarTimestamp = Date.now();

  /** Identifies which edit modal is currently open, or null if none. */
  activeModal: 'basic' | 'about' | 'details' | 'interests' | 'avatar' | null = null;

  /** Whether a save request is currently in flight. */
  isSaving = false;

  /** Reference to the currently open dialog, used to close it programmatically. */
  private dialogRef: MatDialogRef<any> | null = null;

  /** Available career options sourced from profile-setup constants. */
  careers = CAREERS;

  /** Full list of available interests fetched from the API. */
  interestOptions: Interest[] = [];

  /** The student's currently saved interests. */
  interestsList: Interest[] = [];

  /** Draft state for each edit modal, populated when the modal opens. */
  editBasic = { firstName: '', lastName: '', preferredName: '' };
  editAbout = { bio: '' };
  editDetails = {
    career_id: null as number | null,
    grade_level: null as number | null,
  };
  editInterests = {
    selected: [] as Interest[],
  };

  /**
   * Grade level options shown in the details edit dropdown.
   * @readonly
   */
  readonly gradeOptions = [
    { value: 6, label: '6th Grade' },
    { value: 7, label: '7th Grade' },
    { value: 8, label: '8th Grade' },
    { value: 9, label: 'Freshman (9th)' },
    { value: 10, label: 'Sophomore (10th)' },
    { value: 11, label: 'Junior (11th)' },
    { value: 12, label: 'Senior (12th)' },
  ];

  constructor(
    /** Service for setting the browser tab title. */
    private title: Title,
    /** Service for fetching and updating the current user record. */
    private userService: UserService,
    /** Service for fetching and updating the student record. */
    private studentService: StudentService,
    /** Service for fetching and updating the profile record. */
    private profileService: ProfileService,
    /** Service for creating, updating, and fetching the avatar record. */
    private avatarService: AvatarService,
    /** Used to manually trigger change detection after async updates. */
    private cdr: ChangeDetectorRef,
    /** Used to re-enter the Angular zone after async operations that run outside it. */
    private ngZone: NgZone,
    /** Service for opening Material dialogs. */
    private dialog: MatDialog,
    /** Shows a dismissible popup for save errors (e.g. a rejected preferred name). */
    private snackBar: MatSnackBar,
  ) {}

  /**
   * Sets the page title and loads all profile data on init.
   * @returns {void}
   */
  ngOnInit(): void {
    this.title.setTitle('My Profile | Handle');
    this.loadData();
  }

  /**
   * Returns the current authenticated user from UserService.
   * @returns {User | null}
   */
  get currentUser() {
    return this.userService.currentUser;
  }

  /** True when the shared profile page is being used by a faculty member. */
  get isFaculty(): boolean {
    return this.currentUser?.userType === 'faculty';
  }

  /** True when student-specific grade/profile UI should be shown. */
  get isStudent(): boolean {
    return this.currentUser?.userType === 'student';
  }

  /** School name available from the student projection; faculty may not have one. */
  get schoolName(): string {
    return this.student?.School?.name ?? '';
  }

  /**
   * Returns the current user's full name as a trimmed string, falling back to 'Your Name'.
   * @returns {string}
   */
  get fullName(): string {
    const u = this.currentUser;
    if (!u) return 'Your Name';
    return `${(u.preferredName || u.firstName) ?? ''} ${u.lastName ?? ''}`.trim();
  }

  /**
   * Returns the current user's uppercase initials, falling back to '?'.
   * @returns {string}
   */
  get initials(): string {
    const u = this.currentUser;
    if (!u) return '?';
    return (
      `${(u.preferredName || u.firstName)?.[0] ?? ''}${u.lastName?.[0] ?? ''}`.toUpperCase() || '?'
    );
  }

  /**
   * Maps the student's numeric grade level to a human-readable label.
   * @returns {string}
   */
  get gradeLabel(): string {
    const grade = this.student?.grade_level;
    const map: Record<number, string> = {
      6: '6th Grade',
      7: '7th Grade',
      8: '8th Grade',
      9: 'Freshman',
      10: 'Sophomore',
      11: 'Junior',
      12: 'Senior',
    };
    return grade ? (map[grade] ?? `Grade ${grade}`) : '';
  }

  /**
   * Returns the display label for a career by its ID, or an empty string if not found.
   * @param {number | null} id - The career ID to look up.
   * @returns {string}
   */
  getCareerLabel(id: number | null): string {
    if (!id) return '';
    return CAREERS.find((c) => c.id === id)?.label ?? '';
  }

  /**
   * Returns a checklist of profile completion items. Used to drive
   * the progress bar and `completionPercent`.
   * @returns {{ label: string; done: boolean }[]}
   */
  get completionItems() {
    const u = this.currentUser;
    const items = [
      { label: 'Preferred Name', done: !!u?.preferredName },
      { label: 'Name', done: !!(u?.firstName && u?.lastName) },
      { label: 'Interests', done: this.interestsList.length > 0 },
      { label: 'Bio', done: !!this.profile?.bio },
      { label: 'Avatar', done: !!this.avatar },
    ];

    if (this.isStudent) {
      items.splice(
        2,
        0,
        { label: 'Grade', done: !!this.student?.grade_level },
        { label: 'Career', done: !!this.profile?.career_id },
      );
    }

    return items;
  }

  /**
   * Calculates profile completion as a percentage based on `completionItems`.
   * @returns {number} Completion percentage (0–100).
   */
  get completionPercent(): number {
    const done = this.completionItems.filter((i) => i.done).length;
    return Math.round((done / this.completionItems.length) * 100);
  }

  /**
   * Maps the stored `Avatar` model to the `AvatarConfig` shape expected
   * by the AvatarBuilder component, with fallback defaults for missing fields.
   * @returns {Partial<AvatarConfig> | null}
   */
  get currentAvatarConfig(): Partial<AvatarConfig> | null {
    if (!this.avatar) return null;
    return {
      skin: this.avatar.skin ?? '1',
      cheeks: this.avatar.cheeks ?? true,
      hairColor: this.avatar.hair_color ?? 'brown',
      hairFront: this.avatar.hair_front ?? 'regular',
      hairBack: this.avatar.hair_back ?? 'short',
      face: this.avatar.face ?? 'smile',
      shirtStyle: this.avatar.shirt_style ?? 'trousers',
      shirtColor: this.avatar.shirt_color ?? 'blue',
      sleeveLength: this.avatar.sleeve_length ?? 'short',
      pantsStyle: this.avatar.pants_style ?? 'pants',
      pantsColor: this.avatar.pants_color ?? 'blue',
      glasses: this.avatar.glasses ?? 'none',
      beard: this.avatar.beard ?? 'none',
      accessory: this.avatar.accessory ?? 'none',
    };
  }

  /**
   * Opens the edit dialog for the given section. Pre-populates the
   * corresponding `edit*` draft object with current values before opening.
   * @param {'basic' | 'about' | 'details' | 'interests' | 'avatar'} type - The modal section to open.
   * @returns {void}
   */
  openModal(type: 'basic' | 'about' | 'details' | 'interests' | 'avatar'): void {
    if (type === 'details' && this.isFaculty) return;

    const user = this.currentUser;
    this.activeModal = type;

    if (type === 'basic') {
      this.editBasic = {
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        preferredName: user?.preferredName ?? '',
      };
    }
    if (type === 'about') {
      this.editAbout = { bio: this.profile?.bio ?? '' };
    }
    if (type === 'details') {
      this.editDetails = {
        career_id: this.profile?.career_id ?? null,
        grade_level: this.student?.grade_level ?? null,
      };
    }
    if (type === 'interests') {
      this.editInterests = {
        selected: this.interestOptions!.filter((i) =>
          this.interestsList.some((x) => x.id === i.id),
        ),
      };
    }

    const templateMap: Record<string, TemplateRef<any>> = {
      basic: this.basicDialog,
      about: this.aboutDialog,
      details: this.detailsDialog,
      interests: this.interestsDialog,
      avatar: this.avatarDialog,
    };

    this.dialogRef = this.dialog.open(templateMap[type], {
      width: type === 'avatar' ? '700px' : '480px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'profile-dialog',
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.activeModal = null;
      this.isSaving = false;
    });
  }

  /**
   * Closes the currently open dialog.
   * @returns {void}
   */
  closeDialog(): void {
    this.dialogRef?.close();
  }

  /**
   * Runs inside NgZone to ensure change detection fires after async save operations.
   * Resets `isSaving` and closes the dialog.
   * @returns {void}
   */
  private finishSave(): void {
    this.ngZone.run(() => {
      this.isSaving = false;
      this.cdr.detectChanges();
      this.closeDialog();
    });
  }

  /**
   * Runs inside NgZone to reset `isSaving` after a failed save, without
   * closing the dialog — keeps the user's edits on screen so they aren't lost.
   * @returns {void}
   */
  private resetSaving(): void {
    this.ngZone.run(() => {
      this.isSaving = false;
      this.cdr.detectChanges();
    });
  }

  /**
   * Handles the AvatarBuilder's `saved` output. Creates or updates the avatar record,
   * then saves the canvas snapshot as the profile picture if one was provided.
   * Uses create vs update based on whether an avatar already exists.
   * @param {AvatarConfig} config - The completed avatar configuration including the canvas snapshot.
   * @returns {void}
   */
  onAvatarSaved(config: AvatarConfig): void {
    if (!this.profile) return;
    this.isSaving = true;

    const avatarData = {
      skin: config.skin,
      cheeks: config.cheeks,
      hair_color: config.hairColor,
      hair_front: config.hairFront,
      hair_back: config.hairBack,
      face: config.face,
      shirt_style: config.shirtStyle,
      shirt_color: config.shirtColor,
      sleeve_length: config.sleeveLength,
      pants_style: config.pantsStyle,
      pants_color: config.pantsColor,
      glasses: config.glasses,
      beard: config.beard,
      accessory: config.accessory,
    };

    const request$ = this.avatar
      ? this.avatarService.updateAvatar(this.avatar.id, avatarData)
      : this.avatarService.createAvatar(avatarData);

    request$.subscribe({
      next: (saved) => {
        this.ngZone.run(() => {
          this.avatar = saved;
          if (config.snapshot) {
            this.avatarService.saveSnapshot(config.snapshot).subscribe({
              next: (res) => {
                this.ngZone.run(() => {
                  if (this.profile) {
                    this.profile = { ...this.profile, profile_picture_path: res.path };
                    this.avatarTimestamp = Date.now();
                    this.profileService.notifyUpdated(this.profile);
                    this.cdr.detectChanges();
                  }
                  this.finishSave();
                  this.syncProfilePercent();
                });
              },
              error: () => this.finishSave(),
            });
          } else {
            this.finishSave();
            this.syncProfilePercent();
          }
        });
      },
      error: () => this.finishSave(),
    });
  }

  /** Loads current-user profile data and optional student-only metadata. */
  loadData(): void {
    const loadOwnedData = () => {
      this.loadMyProfile();
      if (this.isStudent) this.fetchStudent();
    };

    if (!this.userService.currentUser) {
      this.userService.getCurrentUser().subscribe({
        next: loadOwnedData,
        error: (err) => console.error(err),
      });
    } else {
      loadOwnedData();
    }

    this.interestService.getInterests().subscribe((data) => {
      this.interestOptions = data;
      this.cdr.detectChanges();
    });
    this.interestService.getStudentInterests().subscribe((data) => {
      this.interestsList = data;
      this.cdr.detectChanges();
      this.syncProfilePercent();
    });
  }

  /** Loads student-only grade and school metadata for student users. */
  fetchStudent(): void {
    const user = this.currentUser;
    if (!user || !this.isStudent) return;

    this.studentService.getStudentByUserId(user.id).subscribe({
      next: (student) => {
        this.student = student;
        this.cdr.detectChanges();
        this.syncProfilePercent();
      },
      error: (err) => console.error('Failed to load student data:', err),
    });
  }

  /** Loads or creates the authenticated user's profile, regardless of user type. */
  private loadMyProfile(): void {
    this.profileService.getMyProfile().subscribe({
      next: (res) => {
        if (res.exists && res.profile) {
          this.setLoadedProfile(res.profile);
          return;
        }

        this.profileService.createMyProfile({ profile_complete_percent: 0 }).subscribe({
          next: (profile) => this.setLoadedProfile(profile),
          error: (err) => console.error('Failed to create profile:', err),
        });
      },
      error: (err) => console.error('Failed to load profile:', err),
    });
  }

  /** Applies a loaded profile and fetches its avatar configuration when present. */
  private setLoadedProfile(profile: Profile): void {
    this.profile = profile;
    this.cdr.detectChanges();

    if (profile.avatar_id) {
      this.avatarService.getAvatar(profile.avatar_id).subscribe({
        next: (avatar) => {
          this.avatar = avatar;
          this.cdr.detectChanges();
          this.syncProfilePercent();
        },
        error: (err) => console.error('Failed to load avatar:', err),
      });
    } else {
      this.syncProfilePercent();
    }
  }

  /** Persists the authenticated user's calculated profile completion percentage. */
  syncProfilePercent(): void {
    if (!this.profile) return;
    const percent = this.completionPercent;
    if (this.profile.profile_complete_percent === percent) return;
    this.profileService.updateMyProfile({ profile_complete_percent: percent }).subscribe({
      next: (updated) => {
        if (this.profile) this.profile.profile_complete_percent = updated.profile_complete_percent;
      },
      error: (err) => console.error(err),
    });
  }

  /**
   * Delegates to the appropriate save method based on the currently active modal.
   * @returns {void}
   */
  save(): void {
    const type = this.activeModal;
    if (type === 'basic') this.saveBasic();
    if (type === 'about') this.saveAbout();
    if (type === 'details') this.saveDetails();
    if (type === 'interests') this.saveInterests();
  }

  /**
   * Saves the user's first and last name, then reloads profile data.
   * @returns {void}
   */
  saveBasic(): void {
    const user = this.currentUser;
    if (!user) return;
    this.isSaving = true;
    this.userService
      .updateUser(user.id, {
        preferred_name: this.editBasic.preferredName?.trim() || null,
      } as any)
      .subscribe({
        next: () => {
          this.finishSave();
          this.userService.refreshCurrentUser().subscribe(() => {
            this.cdr.detectChanges();
          });
        },
        error: (err) => {
          this.resetSaving();
          const message = err?.error?.message || 'Failed to save. Please try again.';
          this.snackBar.open(message, 'Dismiss', { duration: 5000 });
        },
      });
  }

  /**
   * Saves the bio field and updates the local profile reference.
   * @returns {void}
   */
  saveAbout(): void {
    if (!this.profile) return;
    this.isSaving = true;
    this.profileService.updateMyProfile({ bio: this.editAbout.bio }).subscribe({
      next: (updated) => {
        this.ngZone.run(() => {
          this.profile = updated;
          this.finishSave();
          this.syncProfilePercent();
        });
      },
      error: (err) => {
        this.resetSaving();
        const message = err?.error?.error || 'Failed to save. Please try again.';
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
      },
    });
  }

  /**
   * Saves career and grade level in parallel via forkJoin, then
   * updates local state for both profile and student.
   * @returns {void}
   */
  saveDetails(): void {
    if (!this.profile || this.isFaculty) return;
    this.isSaving = true;
    this.profileService
      .updateMyProfile({
        career_id: this.editDetails.career_id ?? undefined,
      })
      .subscribe({
        next: (profile) => {
          this.ngZone.run(() => {
            this.profile = profile;
            this.finishSave();
            this.syncProfilePercent();
          });
        },
        error: () => this.finishSave(),
      });
  }

  /**
   * Optimistically updates `interestsList` locally before the API responds,
   * so the UI reflects the change immediately.
   * @returns {void}
   */
  saveInterests(): void {
    if (!this.profile) return;
    this.isSaving = true;
    this.interestsList = this.editInterests.selected;
    this.interestService.studentInterests(this.editInterests.selected).subscribe({
      next: () => {
        this.ngZone.run(() => {
          this.finishSave();
          this.syncProfilePercent();
        });
      },
      error: () => this.finishSave(),
    });
  }

  /**
   * Returns true if the given interest is in the current modal selection.
   * @param {Interest} interest - The interest to check.
   * @returns {boolean}
   */
  isInterestSelected(interest: Interest): boolean {
    return this.editInterests.selected.some((i) => i.id === interest.id);
  }

  /**
   * Adds the interest to the selection if not present, removes it if it is.
   * @param {Interest} interest - The interest to toggle.
   * @returns {void}
   */
  toggleModalInterest(interest: Interest): void {
    const idx = this.editInterests.selected.findIndex((i) => i.id === interest.id);
    if (idx > -1) this.editInterests.selected.splice(idx, 1);
    else this.editInterests.selected.push(interest);
  }
}
